import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { useInternational } from '@/common/locale';
import { type DBType, type TabPanels } from '@/common/utils';
import { DB_TYPE, PLATFORM, ALERT_PLATFORM_TYPES } from '@/common/define/common.define';
import { storeToRefs } from 'pinia';
import { getSlideDetailStore } from '@/common/stores/slide-detail';
import { PlatformAlertInfo } from '@/alert/components/alertDetail/platformAlert';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { usePersistedTab } from '@/common/composables/usePersistedTab';

export type BrokerInfo = {
  dbType: DBType;
  instanceId: string;
  brokerName: string;
};

type BrokerAdditionalData = Omit<BrokerInfo, 'brokerName'>;

export const brokerTabNames = [
  'information',
  'metric',
  'activeCas',
  'brokerLog',
  'sqlLog',
  'alert',
] as const;
export type BrokerTabName = (typeof brokerTabNames)[number];

export const getErrorMessage = (error: any) => {
  const { errorType, message } = error?.response?.data ?? {};
  if (errorType && message) {
    return `${errorType}: ${message}`;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return `${error}`;
};

export const setup = () => {
  const { t } = useInternational();
  const { selectedSlideTabPanel } = storeToRefs(getSlideDetailStore());
  const dbType = computed<DBType>(
    () => (selectedSlideTabPanel.value?.addition as BrokerAdditionalData).dbType ?? DB_TYPE.NONE,
  );
  const brokerInfo = ref<BrokerInfo>({
    dbType: DB_TYPE.NONE,
    instanceId: '',
    brokerName: '',
  });
  const targetId = ref('');
  const alertInfo = ref<PlatformAlertInfo>({
    platform: ALERT_PLATFORM_TYPES.DATABASE,
    targetId: '',
    frameName: FRAME_NAMES.BROKER_DETAIL.ALERT,
  });

  const tabPanels = computed(() => {
    const tabs: TabPanels<BrokerTabName> = [];

    if (dbType.value === DB_TYPE.CUBRID) {
      tabs.push(
        {
          text: t('WORD.INFORMATION'),
          value: 'information',
          component: defineAsyncComponent(
            () =>
              import('@/database/components/brokerDetail/cubrid/information/BrokerInformation.vue'),
          ),
        },
        {
          text: t('WORD.METRIC'),
          value: 'metric',
          component: defineAsyncComponent(
            () => import('@/database/components/brokerDetail/cubrid/metric/BrokerMetricList.vue'),
          ),
        },
        {
          text: `${t('WORD.ACTIVE_CAS')}`,
          value: 'activeCas',
          component: defineAsyncComponent(
            () => import('@/database/components/brokerDetail/cubrid/activeCas/ActiveCas.vue'),
          ),
        },
        {
          text: `${t('WORD.BROKER')} ${t('WORD.LOG')}`,
          value: 'brokerLog',
          component: defineAsyncComponent(
            () => import('@/database/components/brokerDetail/cubrid/brokerLog/BrokerLog.vue'),
          ),
        },
        {
          text: `${t('WORD.SQL')} ${t('WORD.LOG')}`,
          value: 'sqlLog',
          component: defineAsyncComponent(
            () => import('@/database/components/brokerDetail/cubrid/sqlLog/SqlLog.vue'),
          ),
        },
        {
          text: t('WORD.ALERT'),
          value: 'alert',
        },
      );
    }
    return tabs;
  });
  const { getSavedTab, saveTab } = usePersistedTab(
    USER_ENV_KEYS.TAB_database_brokerDetail_selected,
  );
  const selectedTab = ref<string>(getSavedTab({ panels: tabPanels.value }));
  watch(selectedTab, saveTab);

  watch(
    selectedSlideTabPanel,
    (detailInfo) => {
      if (detailInfo === null) return;
      const { dbType: databaseType, instanceId } = detailInfo?.addition as BrokerAdditionalData;
      brokerInfo.value = {
        dbType: databaseType,
        instanceId: instanceId ?? '',
        brokerName: detailInfo?.name ?? '',
      };
      targetId.value = brokerInfo.value.brokerName
        ? `${instanceId}|${brokerInfo.value.brokerName}`
        : instanceId;
      alertInfo.value = {
        platform: PLATFORM.DB,
        targetId: `${instanceId}|${brokerInfo.value.brokerName}`,
        frameName: FRAME_NAMES.BROKER_DETAIL.ALERT,
      };
    },
    { immediate: true },
  );

  return {
    tabPanels,
    selectedTab,
    brokerInfo,
    targetId,
    alertInfo,
    t,
  };
};
